import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Chargement, EnTetePage, Etiquette, Message, Vide } from "@cabinet/charte";
import {
  dossiersApi,
  ErreurApi,
  fournisseursApi,
  type FournisseurVue,
  type ModeDEcheance,
  type SaisieDeFournisseur,
} from "../api/client";
import { codeProposePour } from "../domaine/facture";
import { formaterNombre } from "../domaine/formats";

/**
 * Référentiel des fournisseurs d'un dossier (EF-0501).
 *
 * Le code d'un fournisseur n'est pas un identifiant technique : c'est
 * l'auxiliaire que porteront ses écritures. L'écran le dit, et ne permet pas
 * de le changer après coup — un tiers mal codé se rouvre sous un autre code
 * et l'ancien s'archive, faute de quoi la fiche se dissocierait de son propre
 * grand livre auxiliaire.
 */
export function PageFournisseurs() {
  const { id = "" } = useParams();
  const [recherche, setRecherche] = useState("");
  const [archivesInclus, setArchivesInclus] = useState(false);
  const [enEdition, setEnEdition] = useState<FournisseurVue | "nouveau" | null>(null);

  const dossier = useQuery({ queryKey: ["dossier", id], queryFn: () => dossiersApi.consulter(id) });
  const fournisseurs = useQuery({
    queryKey: ["fournisseurs", id, recherche, archivesInclus],
    queryFn: () => fournisseursApi.lister(id, { recherche: recherche || undefined, archivesInclus }),
  });

  const erreur = dossier.error ?? fournisseurs.error;

  return (
    <>
      <EnTetePage
        surTitre={dossier.data?.raisonSociale ?? "Dossier"}
        titre="Fournisseurs"
        description="Le code d'un fournisseur est l'auxiliaire porté par ses écritures : c'est lui qui fait du compte collectif le compte de ce fournisseur. Ses conditions de règlement donnent l'échéance des factures qui n'en portent pas."
        actions={
          <button type="button" onClick={() => setEnEdition("nouveau")}>
            Ouvrir une fiche
          </button>
        }
      />

      <div className="filtres-plan">
        <input
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
          placeholder="Code ou raison sociale"
          aria-label="Rechercher un fournisseur"
        />
        <label className="discret-texte">
          <input
            type="checkbox"
            checked={archivesInclus}
            onChange={(e) => setArchivesInclus(e.target.checked)}
          />{" "}
          Archivés inclus
        </label>
        {fournisseurs.data != null && (
          <span className="decompte">{formaterNombre(fournisseurs.data.length)} fiches</span>
        )}
      </div>

      {erreur != null && (
        <Message type="erreur" titre={erreur instanceof ErreurApi ? erreur.titre : "Erreur"}>
          <p style={{ margin: "6px 0 0" }}>{erreur.message}</p>
        </Message>
      )}

      {enEdition != null && (
        <FicheFournisseur
          dossier={id}
          fournisseur={enEdition === "nouveau" ? null : enEdition}
          surFermeture={() => setEnEdition(null)}
        />
      )}

      {fournisseurs.isPending && <Chargement quoi="les fournisseurs" />}

      {fournisseurs.data != null && fournisseurs.data.length === 0 && (
        <Vide>
          {recherche === ""
            ? "Aucune fiche fournisseur sur ce dossier. Les écritures peuvent nommer un tiers sans elle ; la fiche lui donne ses conditions de règlement et son échéancier."
            : `Aucun fournisseur ne répond à « ${recherche} ».`}
        </Vide>
      )}

      {fournisseurs.data != null && fournisseurs.data.length > 0 && (
        <div className="tableau-conteneur">
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Raison sociale</th>
                <th>Identifiant légal</th>
                <th>Compte</th>
                <th>Règlement</th>
                <th>Banque</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {fournisseurs.data.map((fournisseur) => (
                <LigneFournisseur
                  key={fournisseur.id}
                  dossier={id}
                  fournisseur={fournisseur}
                  surEdition={() => setEnEdition(fournisseur)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */

function LigneFournisseur({
  dossier,
  fournisseur,
  surEdition,
}: {
  dossier: string;
  fournisseur: FournisseurVue;
  surEdition: () => void;
}) {
  const cache = useQueryClient();
  const statut = useMutation({
    mutationFn: () =>
      fournisseur.statut === "ACTIF"
        ? fournisseursApi.archiver(fournisseur.id)
        : fournisseursApi.reactiver(fournisseur.id),
    onSuccess: () => cache.invalidateQueries({ queryKey: ["fournisseurs", dossier] }),
  });

  return (
    <tr>
      <td className="numero-compte">{fournisseur.code}</td>
      <td>
        {fournisseur.raisonSociale}
        {fournisseur.statut === "ARCHIVE" && (
          <>
            {" "}
            <Etiquette variante="neutre">Archivé</Etiquette>
          </>
        )}
      </td>
      <td className="discret-texte">
        {fournisseur.identifiantPrincipal ?? (
          // L'absence d'identifiant n'empêche rien, mais elle remet en cause
          // le droit à déduction de la TVA : elle se voit ici, avant la facture.
          <Etiquette variante="avertissement">Sans ICE</Etiquette>
        )}
      </td>
      <td className="numero-compte">{fournisseur.compteDeRattachement}</td>
      <td className="discret-texte">
        {fournisseur.conditions.resume}
        {fournisseur.conditions.moyenLibelle != null && ` · ${fournisseur.conditions.moyenLibelle}`}
      </td>
      <td className="discret-texte">
        {fournisseur.coordonneesBancaires?.iban ?? fournisseur.coordonneesBancaires?.rib ?? "—"}
      </td>
      <td className="nombre">
        <button type="button" className="secondaire" onClick={surEdition}>
          Modifier
        </button>{" "}
        <button type="button" className="secondaire" onClick={() => statut.mutate()} disabled={statut.isPending}>
          {fournisseur.statut === "ACTIF" ? "Archiver" : "Réactiver"}
        </button>
      </td>
    </tr>
  );
}

/* ------------------------------------------------------------------ */

interface Champs {
  code: string;
  raisonSociale: string;
  compteDeRattachement: string;
  pays: string;
  ice: string;
  identifiantFiscal: string;
  siren: string;
  modeDEcheance: ModeDEcheance;
  delaiEnJours: string;
  jourDePaiement: string;
  moyenDeReglement: string;
  banque: string;
  rib: string;
  iban: string;
  swift: string;
  ville: string;
  telephone: string;
  courriel: string;
}

const champsDe = (fournisseur: FournisseurVue | null): Champs => ({
  code: fournisseur?.code ?? "",
  raisonSociale: fournisseur?.raisonSociale ?? "",
  compteDeRattachement: fournisseur?.compteDeRattachement ?? "",
  pays: fournisseur?.pays ?? "MA",
  ice: fournisseur?.ice ?? "",
  identifiantFiscal: fournisseur?.identifiantFiscal ?? "",
  siren: fournisseur?.siren ?? "",
  modeDEcheance: fournisseur?.conditions.mode ?? "COMPTANT",
  delaiEnJours: String(fournisseur?.conditions.delaiEnJours ?? 0),
  jourDePaiement: fournisseur?.conditions.jourDePaiement?.toString() ?? "",
  moyenDeReglement: fournisseur?.conditions.moyen ?? "",
  banque: fournisseur?.coordonneesBancaires?.banque ?? "",
  rib: fournisseur?.coordonneesBancaires?.rib ?? "",
  iban: fournisseur?.coordonneesBancaires?.iban ?? "",
  swift: fournisseur?.coordonneesBancaires?.swift ?? "",
  ville: fournisseur?.contact?.ville ?? "",
  telephone: fournisseur?.contact?.telephone ?? "",
  courriel: fournisseur?.contact?.courriel ?? "",
});

/**
 * Fiche d'ouverture ou de modification.
 *
 * Le compte de rattachement laissé vide prend celui du journal d'achats du
 * dossier : c'est une donnée du référentiel, et l'écran n'a pas à connaître
 * de numéro.
 */
function FicheFournisseur({
  dossier,
  fournisseur,
  surFermeture,
}: {
  dossier: string;
  fournisseur: FournisseurVue | null;
  surFermeture: () => void;
}) {
  const cache = useQueryClient();
  const [champs, setChamps] = useState<Champs>(() => champsDe(fournisseur));
  const options = useQuery({ queryKey: ["fournisseurs-options"], queryFn: fournisseursApi.options });

  const modifier = <C extends keyof Champs>(nom: C, valeur: Champs[C]) =>
    setChamps((precedents) => ({ ...precedents, [nom]: valeur }));

  const enregistrement = useMutation({
    mutationFn: (saisie: SaisieDeFournisseur) =>
      fournisseur == null
        ? fournisseursApi.ouvrir(dossier, saisie)
        : fournisseursApi.modifier(fournisseur.id, saisie),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ["fournisseurs", dossier] });
      surFermeture();
    },
  });

  const codePropose = champs.code.trim() === "" ? codeProposePour(champs.raisonSociale) : champs.code.trim();
  const avecJour = champs.modeDEcheance === "JOURS_FIN_DE_MOIS_LE";
  const avecDelai = champs.modeDEcheance !== "COMPTANT";

  function envoyer() {
    enregistrement.mutate({
      code: fournisseur?.code ?? (champs.code.trim() || null),
      raisonSociale: champs.raisonSociale.trim(),
      compteDeRattachement: champs.compteDeRattachement.trim() || null,
      pays: champs.pays || null,
      ice: champs.pays === "MA" ? champs.ice.trim() || null : null,
      identifiantFiscal: champs.pays === "MA" ? champs.identifiantFiscal.trim() || null : null,
      siren: champs.pays === "FR" ? champs.siren.trim() || null : null,
      modeDEcheance: champs.modeDEcheance,
      delaiEnJours: avecDelai ? Number(champs.delaiEnJours || 0) : 0,
      jourDePaiement: avecJour && champs.jourDePaiement !== "" ? Number(champs.jourDePaiement) : null,
      moyenDeReglement: champs.moyenDeReglement || null,
      banque: champs.banque.trim() || null,
      rib: champs.rib.trim() || null,
      iban: champs.iban.trim() || null,
      swift: champs.swift.trim() || null,
      ville: champs.ville.trim() || null,
      telephone: champs.telephone.trim() || null,
      courriel: champs.courriel.trim() || null,
    });
  }

  return (
    <form
      className="fiche-fournisseur"
      onSubmit={(e) => {
        e.preventDefault();
        envoyer();
      }}
    >
      <h3>{fournisseur == null ? "Nouvelle fiche fournisseur" : `Fiche ${fournisseur.code}`}</h3>

      <div className="champs">
        <label>
          Raison sociale
          <input
            value={champs.raisonSociale}
            onChange={(e) => modifier("raisonSociale", e.target.value)}
            required
            autoFocus
          />
        </label>
        <label>
          Code d'auxiliaire
          <input
            value={champs.code}
            onChange={(e) => modifier("code", e.target.value.toUpperCase())}
            placeholder={codePropose || "ATLAS"}
            disabled={fournisseur != null}
            maxLength={24}
          />
          <span className="aide">
            {fournisseur != null
              ? "Le code ne change pas : les écritures passées le portent déjà."
              : `Porté par chaque écriture du fournisseur. Laissé vide : ${codePropose || "—"}.`}
          </span>
        </label>
        <label>
          Compte de rattachement
          <input
            value={champs.compteDeRattachement}
            onChange={(e) => modifier("compteDeRattachement", e.target.value)}
            placeholder="Contrepartie du journal d'achats"
          />
        </label>

        <label>
          Pays
          <select value={champs.pays} onChange={(e) => modifier("pays", e.target.value)}>
            <option value="">Sans identifiant légal</option>
            <option value="MA">Maroc</option>
            <option value="FR">France</option>
          </select>
        </label>
        {champs.pays === "MA" && (
          <>
            <label>
              ICE
              <input value={champs.ice} onChange={(e) => modifier("ice", e.target.value)} maxLength={15} />
            </label>
            <label>
              Identifiant fiscal
              <input
                value={champs.identifiantFiscal}
                onChange={(e) => modifier("identifiantFiscal", e.target.value)}
              />
            </label>
          </>
        )}
        {champs.pays === "FR" && (
          <label>
            SIREN
            <input value={champs.siren} onChange={(e) => modifier("siren", e.target.value)} maxLength={9} />
          </label>
        )}

        <label>
          Échéance
          <select
            value={champs.modeDEcheance}
            onChange={(e) => modifier("modeDEcheance", e.target.value as ModeDEcheance)}
          >
            {(options.data?.modesDEcheance ?? []).map((mode) => (
              <option key={mode.code} value={mode.code}>
                {mode.libelle}
              </option>
            ))}
          </select>
        </label>
        {avecDelai && (
          <label>
            Délai en jours
            <input
              type="number"
              min={0}
              max={365}
              value={champs.delaiEnJours}
              onChange={(e) => modifier("delaiEnJours", e.target.value)}
            />
          </label>
        )}
        {avecJour && (
          <label>
            Payé le
            <input
              type="number"
              min={1}
              max={31}
              value={champs.jourDePaiement}
              onChange={(e) => modifier("jourDePaiement", e.target.value)}
            />
          </label>
        )}
        <label>
          Moyen de règlement
          <select
            value={champs.moyenDeReglement}
            onChange={(e) => modifier("moyenDeReglement", e.target.value)}
          >
            <option value="">—</option>
            {(options.data?.moyensDeReglement ?? []).map((moyen) => (
              <option key={moyen.code} value={moyen.code}>
                {moyen.libelle}
              </option>
            ))}
          </select>
        </label>

        <label>
          Banque
          <input value={champs.banque} onChange={(e) => modifier("banque", e.target.value)} />
        </label>
        <label>
          RIB
          <input value={champs.rib} onChange={(e) => modifier("rib", e.target.value)} />
        </label>
        <label>
          IBAN
          <input value={champs.iban} onChange={(e) => modifier("iban", e.target.value)} />
          <span className="aide">Sa clé se vérifie hors ligne : c'est le seul contrôle réel d'un compte.</span>
        </label>
        <label>
          SWIFT
          <input value={champs.swift} onChange={(e) => modifier("swift", e.target.value)} maxLength={11} />
        </label>

        <label>
          Ville
          <input value={champs.ville} onChange={(e) => modifier("ville", e.target.value)} />
        </label>
        <label>
          Téléphone
          <input value={champs.telephone} onChange={(e) => modifier("telephone", e.target.value)} />
        </label>
        <label>
          Courriel
          <input type="email" value={champs.courriel} onChange={(e) => modifier("courriel", e.target.value)} />
        </label>
      </div>

      {enregistrement.error != null && (
        <Message
          type="erreur"
          titre={enregistrement.error instanceof ErreurApi ? enregistrement.error.titre : "Erreur"}
        >
          <p style={{ margin: "6px 0 0" }}>{enregistrement.error.message}</p>
        </Message>
      )}

      <div className="barre-actions">
        <button type="submit" disabled={enregistrement.isPending || champs.raisonSociale.trim() === ""}>
          {fournisseur == null ? "Ouvrir la fiche" : "Enregistrer"}
        </button>
        <button type="button" className="secondaire" onClick={surFermeture}>
          Annuler
        </button>
      </div>
    </form>
  );
}
